// src/components/AdminPaymentSettings.tsx
import React, { useState, useEffect } from 'react';
import { DollarSign, Percent, Plus, Trash2, Edit2, Save, X } from 'lucide-react';

interface PricingTier {
  id: string;
  name: string;
  type: 'one-time' | 'monthly' | 'yearly';
  price: number;
  description: string;
  features: string[];
  active: boolean;
}

interface PromoCode {
  id: string;
  code: string;
  type: 'percentage' | 'fixed';
  value: number;
  description: string;
  maxUses?: number;
  usedCount: number;
  expiresAt?: string;
  active: boolean;
  applicableTiers: string[];
}

const STORAGE_KEY_PRICING = 'whipt_admin_pricing';
const STORAGE_KEY_PROMOS = 'whipt_admin_promos';

const DEFAULT_PRICING: PricingTier[] = [
  {
    id: 'bbq-donation',
    name: 'BBQ Community Donation',
    type: 'one-time',
    price: 4.99,
    description: 'One-time donation - Single user lifetime access to both organizers',
    features: [
      'Lifetime access to both organizers',
      'All features included',
      'Virtual rooms & messaging',
      'Support BBQ Community non-profit',
      'Single user account'
    ],
    active: true
  },
  {
    id: 'student-monthly',
    name: 'Student Organizer',
    type: 'monthly',
    price: 4.99,
    description: 'Individual subscription - Student Organizer only',
    features: [
      'Student Organizer access',
      'All student features',
      'Virtual study rooms',
      'Assignment tracking',
      'Single user account'
    ],
    active: true
  },
  {
    id: 'student-yearly',
    name: 'Student Organizer (Annual)',
    type: 'yearly',
    price: 49.99,
    description: 'Individual yearly subscription - Student Organizer only',
    features: [
      'Student Organizer access',
      'All student features',
      'Save $9.89/year',
      'Virtual study rooms',
      'Single user account'
    ],
    active: true
  },
  {
    id: 'personal-monthly',
    name: 'Personal Organizer',
    type: 'monthly',
    price: 4.99,
    description: 'Individual subscription - Personal Organizer only',
    features: [
      'Personal Organizer access',
      'All personal features',
      'Budget tracking',
      'Life management tools',
      'Single user account'
    ],
    active: true
  },
  {
    id: 'personal-yearly',
    name: 'Personal Organizer (Annual)',
    type: 'yearly',
    price: 49.99,
    description: 'Individual yearly subscription - Personal Organizer only',
    features: [
      'Personal Organizer access',
      'All personal features',
      'Save $9.89/year',
      'Budget tracking',
      'Single user account'
    ],
    active: true
  },
  {
    id: 'both-monthly',
    name: 'Both Organizers',
    type: 'monthly',
    price: 7.99,
    description: 'Individual subscription - Both organizers for single user',
    features: [
      'Student + Personal Organizers',
      'All features included',
      'Save $1.99/month',
      'Switch between organizers',
      'Single user account'
    ],
    active: true
  },
  {
    id: 'both-yearly',
    name: 'Both Organizers (Annual)',
    type: 'yearly',
    price: 79.99,
    description: 'Individual yearly subscription - Both organizers for single user',
    features: [
      'Student + Personal Organizers',
      'All features included',
      'Save $19.89/year',
      'Switch between organizers',
      'Single user account'
    ],
    active: true
  },
  {
    id: 'family-2-monthly',
    name: 'Family Bundle - 2 Users',
    type: 'monthly',
    price: 12.99,
    description: 'Household subscription - 2 users, any mix of organizers',
    features: [
      '2 separate user accounts',
      'Any organizer combination',
      'Save 20% vs individual',
      'Perfect for parent + child',
      'Separate logins per user'
    ],
    active: true
  },
  {
    id: 'family-2-yearly',
    name: 'Family Bundle - 2 Users (Annual)',
    type: 'yearly',
    price: 129.99,
    description: 'Household yearly subscription - 2 users, any mix',
    features: [
      '2 separate user accounts',
      'Any organizer combination',
      'Save 20% vs individual',
      'Save additional $25.89/year',
      'Separate logins per user'
    ],
    active: true
  },
  {
    id: 'family-3-monthly',
    name: 'Family Bundle - 3 Users',
    type: 'monthly',
    price: 17.99,
    description: 'Household subscription - 3 users, any mix of organizers',
    features: [
      '3 separate user accounts',
      'Any organizer combination',
      'Save 25% vs individual',
      'Perfect for families',
      'Separate logins per user'
    ],
    active: true
  },
  {
    id: 'family-3-yearly',
    name: 'Family Bundle - 3 Users (Annual)',
    type: 'yearly',
    price: 179.99,
    description: 'Household yearly subscription - 3 users, any mix',
    features: [
      '3 separate user accounts',
      'Any organizer combination',
      'Save 25% vs individual',
      'Save additional $35.89/year',
      'Separate logins per user'
    ],
    active: true
  },
  {
    id: 'family-4-monthly',
    name: 'Family Bundle - 4 Users',
    type: 'monthly',
    price: 21.99,
    description: 'Household subscription - 4 users, any mix of organizers',
    features: [
      '4 separate user accounts',
      'Any organizer combination',
      'Save 30% vs individual',
      'Best value for families',
      'Separate logins per user'
    ],
    active: true
  },
  {
    id: 'family-4-yearly',
    name: 'Family Bundle - 4 Users (Annual)',
    type: 'yearly',
    price: 219.99,
    description: 'Household yearly subscription - 4 users, any mix',
    features: [
      '4 separate user accounts',
      'Any organizer combination',
      'Save 30% vs individual',
      'Save additional $43.89/year',
      'Separate logins per user'
    ],
    active: true
  }
];

const DEFAULT_PROMOS: PromoCode[] = [
  {
    id: 'beta100',
    code: 'BETA100',
    type: 'percentage',
    value: 100,
    description: 'Beta tester - 100% off BBQ donation',
    maxUses: 100,
    usedCount: 0,
    active: true,
    applicableTiers: ['bbq-donation']
  },
  {
    id: 'student20',
    code: 'STUDENT20',
    type: 'percentage',
    value: 20,
    description: 'Student discount - 20% off subscriptions',
    active: true,
    applicableTiers: ['pro-monthly', 'premium-monthly']
  }
];

export function AdminPaymentSettings() {
  const [pricing, setPricing] = useState<PricingTier[]>([]);
  const [promos, setPromos] = useState<PromoCode[]>([]);
  const [editingTier, setEditingTier] = useState<string | null>(null);
  const [editingPromo, setEditingPromo] = useState<string | null>(null);
  const [showNewTier, setShowNewTier] = useState(false);
  const [showNewPromo, setShowNewPromo] = useState(false);

  // Load data
  useEffect(() => {
    const savedPricing = localStorage.getItem(STORAGE_KEY_PRICING);
    const savedPromos = localStorage.getItem(STORAGE_KEY_PROMOS);
    
    setPricing(savedPricing ? JSON.parse(savedPricing) : DEFAULT_PRICING);
    setPromos(savedPromos ? JSON.parse(savedPromos) : DEFAULT_PROMOS);
  }, []);

  // Save pricing
  const savePricing = (newPricing: PricingTier[]) => {
    setPricing(newPricing);
    localStorage.setItem(STORAGE_KEY_PRICING, JSON.stringify(newPricing));
  };

  // Save promos
  const savePromos = (newPromos: PromoCode[]) => {
    setPromos(newPromos);
    localStorage.setItem(STORAGE_KEY_PROMOS, JSON.stringify(newPromos));
  };

  // Update tier
  const updateTier = (id: string, updates: Partial<PricingTier>) => {
    const newPricing = pricing.map(tier => 
      tier.id === id ? { ...tier, ...updates } : tier
    );
    savePricing(newPricing);
    setEditingTier(null);
  };

  // Update promo
  const updatePromo = (id: string, updates: Partial<PromoCode>) => {
    const newPromos = promos.map(promo => 
      promo.id === id ? { ...promo, ...updates } : promo
    );
    savePromos(newPromos);
    setEditingPromo(null);
  };

  // Add new tier
  const addTier = (tier: PricingTier) => {
    savePricing([...pricing, tier]);
    setShowNewTier(false);
  };

  // Add new promo
  const addPromo = (promo: PromoCode) => {
    savePromos([...promos, promo]);
    setShowNewPromo(false);
  };

  // Delete tier
  const deleteTier = (id: string) => {
    if (confirm('Delete this pricing tier?')) {
      savePricing(pricing.filter(t => t.id !== id));
    }
  };

  // Delete promo
  const deletePromo = (id: string) => {
    if (confirm('Delete this promo code?')) {
      savePromos(promos.filter(p => p.id !== id));
    }
  };

  return (
    <div className="space-y-6 text-white">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Payment & Pricing Settings</h2>
        <div className="text-sm text-slate-400">
          Admin Controls
        </div>
      </div>

      {/* Pricing Tiers Section */}
      <section className="bg-gradient-to-br from-slate-900/50 to-slate-800/50 rounded-2xl border border-white/10 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-400" />
            <h3 className="text-xl font-bold">Pricing Tiers</h3>
          </div>
          <button
            onClick={() => setShowNewTier(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Tier
          </button>
        </div>

        <div className="space-y-4">
          {pricing.map(tier => (
            <div key={tier.id} className="bg-black/20 rounded-xl border border-white/10 p-4">
              {editingTier === tier.id ? (
                <TierEditor 
                  tier={tier}
                  onSave={(updates) => updateTier(tier.id, updates)}
                  onCancel={() => setEditingTier(null)}
                />
              ) : (
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-semibold">{tier.name}</h4>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        tier.type === 'one-time' ? 'bg-blue-500/20 text-blue-300' :
                        tier.type === 'monthly' ? 'bg-purple-500/20 text-purple-300' :
                        'bg-amber-500/20 text-amber-300'
                      }`}>
                        {tier.type}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        tier.active ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'
                      }`}>
                        {tier.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-emerald-400 mb-2">
                      ${tier.price.toFixed(2)}
                      {tier.type !== 'one-time' && <span className="text-sm text-slate-400">/{tier.type}</span>}
                    </p>
                    <p className="text-sm text-slate-300 mb-3">{tier.description}</p>
                    <div className="text-xs text-slate-400 space-y-1">
                      {tier.features.map((feature, idx) => (
                        <div key={idx}>✓ {feature}</div>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingTier(tier.id)}
                      className="p-2 rounded-lg bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 transition-colors"
                      title="Edit tier"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteTier(tier.id)}
                      className="p-2 rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/30 transition-colors"
                      title="Delete tier"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {showNewTier && (
          <NewTierForm
            onSave={addTier}
            onCancel={() => setShowNewTier(false)}
          />
        )}
      </section>

      {/* Promo Codes Section */}
      <section className="bg-gradient-to-br from-slate-900/50 to-slate-800/50 rounded-2xl border border-white/10 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Percent className="w-5 h-5 text-purple-400" />
            <h3 className="text-xl font-bold">Promo Codes & Discounts</h3>
          </div>
          <button
            onClick={() => setShowNewPromo(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Promo
          </button>
        </div>

        <div className="space-y-4">
          {promos.map(promo => (
            <div key={promo.id} className="bg-black/20 rounded-xl border border-white/10 p-4">
              {editingPromo === promo.id ? (
                <PromoEditor
                  promo={promo}
                  tiers={pricing}
                  onSave={(updates) => updatePromo(promo.id, updates)}
                  onCancel={() => setEditingPromo(null)}
                />
              ) : (
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <code className="px-3 py-1 rounded-lg bg-purple-500/20 text-purple-200 font-mono font-bold">
                        {promo.code}
                      </code>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        promo.active ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'
                      }`}>
                        {promo.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="text-lg font-semibold text-purple-300 mb-1">
                      {promo.type === 'percentage' ? `${promo.value}% OFF` : `$${promo.value.toFixed(2)} OFF`}
                    </p>
                    <p className="text-sm text-slate-300 mb-2">{promo.description}</p>
                    <div className="flex flex-wrap gap-4 text-xs text-slate-400">
                      {promo.maxUses && (
                        <span>Uses: {promo.usedCount}/{promo.maxUses}</span>
                      )}
                      {promo.expiresAt && (
                        <span>Expires: {new Date(promo.expiresAt).toLocaleDateString()}</span>
                      )}
                      <span>Applies to: {promo.applicableTiers.join(', ')}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingPromo(promo.id)}
                      className="p-2 rounded-lg bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 transition-colors"
                      title="Edit promo"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deletePromo(promo.id)}
                      className="p-2 rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/30 transition-colors"
                      title="Delete promo"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {showNewPromo && (
          <NewPromoForm
            tiers={pricing}
            onSave={addPromo}
            onCancel={() => setShowNewPromo(false)}
          />
        )}
      </section>

      {/* Refund Policy Section */}
      <section className="bg-gradient-to-br from-slate-900/50 to-slate-800/50 rounded-2xl border border-white/10 p-6">
        <div className="flex items-center gap-2 mb-6">
          <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-xl font-bold">Refund Policy</h3>
        </div>

        <div className="space-y-4">
          {/* Beta Free Access Notice */}
          <div className="bg-green-500/10 border-2 border-green-500/50 rounded-xl p-4">
            <h4 className="font-semibold text-green-300 mb-2 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Beta Period - NO PAYMENTS COLLECTED
            </h4>
            <div className="text-sm text-green-200/90 space-y-2">
              <p><strong>Current Status:</strong> All users have FREE ACCESS during beta testing. No payments, donations, or subscriptions are being collected.</p>
              <p><strong>No Refunds Needed:</strong> Since there are no charges during beta, there are no refunds to process.</p>
            </div>
          </div>

          {/* Post-Beta Refund Policy */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            <h4 className="font-semibold text-blue-300 mb-3">Post-Beta Refund Policy (When Payments Begin)</h4>
            <div className="space-y-3 text-sm text-slate-300">
              <div className="flex gap-3">
                <span className="text-blue-400 font-bold">•</span>
                <p><strong>BBQ Community Donation (One-time $4.99):</strong> Non-refundable as it's a charitable donation to support BBQ Community non-profit organization.</p>
              </div>
              <div className="flex gap-3">
                <span className="text-blue-400 font-bold">•</span>
                <p><strong>Monthly Subscriptions:</strong> Cancel anytime. No refunds for partial months, but access continues until end of billing period.</p>
              </div>
              <div className="flex gap-3">
                <span className="text-blue-400 font-bold">•</span>
                <p><strong>Yearly Subscriptions:</strong> Refundable within 14 days of purchase for full refund. After 14 days, no refunds but can cancel to prevent renewal.</p>
              </div>
              <div className="flex gap-3">
                <span className="text-blue-400 font-bold">•</span>
                <p><strong>First 30 Days After Beta:</strong> Special grace period - full refunds available for any reason within 30 days of first paid purchase.</p>
              </div>
            </div>
          </div>

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
            <h4 className="font-semibold text-amber-300 mb-2">Refund Request Process</h4>
            <ol className="list-decimal list-inside space-y-2 text-sm text-slate-300 ml-2">
              <li>User emails support with refund request and reason</li>
              <li>Admin reviews request within 24-48 hours</li>
              <li>If approved, refund processed through Stripe within 5-10 business days</li>
              <li>User receives confirmation email when refund is issued</li>
            </ol>
          </div>

          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <h4 className="font-semibold text-slate-300 mb-3">Admin Refund Actions</h4>
            <div className="space-y-2 text-sm text-slate-400">
              <p><strong>To process a refund:</strong></p>
              <ol className="list-decimal list-inside space-y-1 ml-4">
                <li>Log into Stripe Dashboard</li>
                <li>Find the payment in Payments section</li>
                <li>Click "Refund" and select full or partial</li>
                <li>Add internal note about reason</li>
                <li>Confirm refund</li>
                <li>Update user status in admin panel if needed</li>
              </ol>
              <div className="mt-4 pt-4 border-t border-slate-700">
                <a 
                  href="https://dashboard.stripe.com/payments" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Open Stripe Dashboard
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// Tier Editor Component
function TierEditor({ tier, onSave, onCancel }: { tier: PricingTier; onSave: (updates: Partial<PricingTier>) => void; onCancel: () => void }) {
  const [formData, setFormData] = useState(tier);

  return (
    <div className="space-y-3">
      <input
        type="text"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white"
        placeholder="Tier name"
      />
      <input
        type="number"
        step="0.01"
        value={formData.price}
        onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
        className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white"
        placeholder="Price"
      />
      <textarea
        value={formData.description}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white h-20"
        placeholder="Description"
      />
      <div className="flex gap-2">
        <button
          onClick={() => onSave(formData)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30"
        >
          <Save className="w-4 h-4" />
          Save
        </button>
        <button
          onClick={onCancel}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/30"
        >
          <X className="w-4 h-4" />
          Cancel
        </button>
      </div>
    </div>
  );
}

// Promo Editor Component
function PromoEditor({ promo, tiers, onSave, onCancel }: { promo: PromoCode; tiers: PricingTier[]; onSave: (updates: Partial<PromoCode>) => void; onCancel: () => void }) {
  const [formData, setFormData] = useState(promo);

  return (
    <div className="space-y-3">
      <input
        type="text"
        value={formData.code}
        onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
        className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white font-mono"
        placeholder="PROMO CODE"
      />
      <div className="grid grid-cols-2 gap-3">
        <select
          value={formData.type}
          onChange={(e) => setFormData({ ...formData, type: e.target.value as 'percentage' | 'fixed' })}
          className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white"
        >
          <option value="percentage">Percentage</option>
          <option value="fixed">Fixed Amount</option>
        </select>
        <input
          type="number"
          step="0.01"
          value={formData.value}
          onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) })}
          className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white"
          placeholder="Value"
        />
      </div>
      <textarea
        value={formData.description}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white h-16"
        placeholder="Description"
      />
      <div className="flex gap-2">
        <button
          onClick={() => onSave(formData)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30"
        >
          <Save className="w-4 h-4" />
          Save
        </button>
        <button
          onClick={onCancel}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/30"
        >
          <X className="w-4 h-4" />
          Cancel
        </button>
      </div>
    </div>
  );
}

// New Tier Form (simplified)
function NewTierForm({ onSave, onCancel }: { onSave: (tier: PricingTier) => void; onCancel: () => void }) {
  const [formData, setFormData] = useState<PricingTier>({
    id: `tier-${Date.now()}`,
    name: '',
    type: 'monthly',
    price: 0,
    description: '',
    features: [],
    active: true
  });

  return (
    <div className="mt-4 p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/30">
      <h4 className="font-semibold mb-3">New Pricing Tier</h4>
      <div className="space-y-3">
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white"
          placeholder="Tier name"
        />
        <input
          type="number"
          step="0.01"
          value={formData.price}
          onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
          className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white"
          placeholder="Price"
        />
        <select
          value={formData.type}
          onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
          className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white"
        >
          <option value="one-time">One-time</option>
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
        </select>
        <div className="flex gap-2">
          <button
            onClick={() => formData.name && formData.price && onSave(formData)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30"
          >
            <Save className="w-4 h-4" />
            Create Tier
          </button>
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/30"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// New Promo Form (simplified)
function NewPromoForm({ tiers, onSave, onCancel }: { tiers: PricingTier[]; onSave: (promo: PromoCode) => void; onCancel: () => void }) {
  const [formData, setFormData] = useState<PromoCode>({
    id: `promo-${Date.now()}`,
    code: '',
    type: 'percentage',
    value: 0,
    description: '',
    usedCount: 0,
    active: true,
    applicableTiers: []
  });

  return (
    <div className="mt-4 p-4 bg-purple-500/10 rounded-xl border border-purple-500/30">
      <h4 className="font-semibold mb-3">New Promo Code</h4>
      <div className="space-y-3">
        <input
          type="text"
          value={formData.code}
          onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
          className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white font-mono"
          placeholder="PROMOCODE"
        />
        <div className="grid grid-cols-2 gap-3">
          <select
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
            className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white"
          >
            <option value="percentage">Percentage</option>
            <option value="fixed">Fixed Amount</option>
          </select>
          <input
            type="number"
            step="0.01"
            value={formData.value}
            onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) })}
            className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white"
            placeholder="Value"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => formData.code && formData.value && onSave(formData)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-500/20 text-purple-300 hover:bg-purple-500/30"
          >
            <Save className="w-4 h-4" />
            Create Promo
          </button>
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/30"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminPaymentSettings;
